// addPartition — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-partition', table, partition })` op (DSE2 §3.E1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1 — Partitioning UI.
//
// Per-engine surface (mirrors DSE2's `synthesizeAddPartition`):
//   - pgsql:  ALTER TABLE t ATTACH PARTITION child FOR VALUES … (the partition
//             is itself a child table previously declared with PARTITION OF).
//             Requires a non-empty boundary clause.
//   - mysql:  ALTER TABLE t ADD PARTITION (PARTITION p …).
//   - oracle: ALTER TABLE t ADD PARTITION p ….
//   - mssql:  rejects — mssql adds partitions by ALTER PARTITION FUNCTION
//             SPLIT/MERGE RANGE, out of v2 Slice E1 scope.
//   - sqlite: rejects — no partitioning support.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { QualifiedName } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  PartitionDef,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AddPartitionArgs {
  readonly table: QualifiedName;
  readonly partition: PartitionDef;
}

export function buildAddPartitionEdit(args: AddPartitionArgs): DdlEdit {
  return { op: 'add-partition', table: args.table, partition: args.partition };
}

export async function commitAddPartition(
  ctx: VqeDdlEditContext,
  args: AddPartitionArgs,
): Promise<void> {
  const edit = buildAddPartitionEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ADD_PARTITION_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'postgres', 'mysql', 'oracle',
] as const);

export function isAddPartitionSupported(engine: SqlEngine): boolean {
  return ADD_PARTITION_SUPPORTED.has(engine);
}

export function getAddPartitionUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return (
      'SQL Server adds partitions by altering the partition function — use '
      + 'ALTER PARTITION FUNCTION pf SPLIT RANGE / MERGE RANGE on the bound '
      + 'function. Direct partition add/drop on the table is not supported.'
    );
  }
  if (engine === 'sqlite') {
    return 'SQLite has no partitioning support.';
  }
  return '';
}

/** Pre-flight validator. Returns null when args are acceptable, else a
 *  user-facing message. */
export function validateAddPartitionForEngine(
  engine: SqlEngine,
  args: AddPartitionArgs,
): string | null {
  if (!isAddPartitionSupported(engine)) {
    return getAddPartitionUnsupportedTooltip(engine);
  }
  const p = args.partition;
  if (p.name.trim().length === 0) {
    return 'Partition name is required.';
  }
  if (engine === 'postgres') {
    const k = p.boundary.kind;
    if (k === 'hash' || k === 'system') {
      return (
        'PostgreSQL ATTACH PARTITION requires a FOR VALUES boundary — hash and '
        + 'system boundaries are not valid for ATTACH.'
      );
    }
  }
  if (p.boundary.kind === 'range-from-to' && engine !== 'postgres') {
    return (
      `${engine} does not accept range FROM/TO partition boundaries — only `
      + `PostgreSQL supports FOR VALUES FROM (…) TO (…). Use VALUES LESS THAN `
      + `(…) on MySQL / Oracle.`
    );
  }
  if (p.boundary.kind === 'list-default' && engine !== 'postgres') {
    return (
      `${engine} does not accept DEFAULT list partitions — only PostgreSQL `
      + `supports FOR VALUES DEFAULT.`
    );
  }
  return null;
}
